import { css } from '@linaria/core';
import React, { cloneElement, createElement, forwardRef, isValidElement } from 'react';

import { clsx } from '@creditornot/cb-utils';

export const sizes = ['tiny', 'small', 'medium'] as const;

const mappedSourceSizes = {
  medium: 'medium',
  small: 'small',
  tiny: 'small',
} as const;

export const pxSizeMap = {
  medium: 24,
  small: 16,
  tiny: 12,
};

export type IconSize = (typeof sizes)[number];

type SvgComponentProps = React.SVGProps<SVGSVGElement>;
type SvgElement = React.ReactElement<SvgComponentProps>;

export type AlchemyIconComponent = React.ComponentType<SvgComponentProps> & {
  __al_flipOnRtl?: boolean;
  __al_size: 'small' | 'medium';
};

interface OptionalSizeProps {
  size?: IconSize;
  source: AlchemyIconComponent;
}
interface AlchemyIcon {
  medium: AlchemyIconComponent;
  small: AlchemyIconComponent;
}
interface RequiredSizeProps {
  size: IconSize;
  source: SvgElement | AlchemyIcon | AlchemyIconComponent | React.ComponentType<SvgComponentProps>;
}

export type Props = SvgComponentProps & {
  /**
   * By default, the Alchemy icons are scaled to 1.15 to match the size of the legacy icons.
   *
   * @default false
   */
  disableAlchemyIconCompatibilityScale?: boolean;
  /**
   * Used to automatically flip a custom icon in RTL mode.
   */
  flipOnRtl?: boolean;
} & (OptionalSizeProps | RequiredSizeProps);

export type IconSource = Props['source'];

const rtlFlipClassName = css`
  :dir(rtl) & {
    transform: scaleX(-1);
  }
`;

function isAlchemyIconComponent(element: unknown): element is AlchemyIconComponent {
  return (
    typeof element === 'object' &&
    element !== null &&
    '__al_size' in element &&
    typeof element.__al_size === 'string' &&
    ['medium', 'small'].includes(element.__al_size)
  );
}

function isAlchemyIcon(source: unknown): source is AlchemyIcon {
  return (
    typeof source === 'object' &&
    source !== null &&
    'small' in source &&
    'medium' in source &&
    isAlchemyIconComponent(source.small) &&
    isAlchemyIconComponent(source.medium)
  );
}

export const Icon = forwardRef<SVGSVGElement, Props>(function Icon(
  { disableAlchemyIconCompatibilityScale, flipOnRtl = false, size, source, ...rest },
  ref,
) {
  const props = {
    fill: 'currentColor',
    role: 'presentation',
    ...rest,
    ref,
  } satisfies SvgComponentProps;

  const iconSourceSize = size ?? 'medium';
  const iconPxSize = pxSizeMap[iconSourceSize];

  if (isAlchemyIconComponent(source)) {
    const componentSize = size ? iconPxSize : pxSizeMap[source.__al_size];

    return createElement(source, {
      ...props,
      width: props.width ?? componentSize,
      height: props.height ?? componentSize,
      className: clsx(props.className, source.__al_flipOnRtl && rtlFlipClassName),
      style: disableAlchemyIconCompatibilityScale
        ? props.style
        : {
            scale: '1.15',
            ...props.style,
          },
    });
  }

  if (isAlchemyIcon(source)) {
    const component = source[mappedSourceSizes[iconSourceSize]];

    return createElement(component, {
      ...props,
      width: props.width ?? iconPxSize,
      height: props.height ?? iconPxSize,
      className: clsx(props.className, component.__al_flipOnRtl && rtlFlipClassName),
      style: disableAlchemyIconCompatibilityScale
        ? props.style
        : {
            scale: '1.15',
            ...props.style,
          },
    });
  }

  if (isValidElement(source)) {
    const svgElement = React.Children.only(source);

    return cloneElement(svgElement, {
      ...props,
      width: props.width ?? (size ? pxSizeMap[size] : (svgElement.props.width ?? iconPxSize)),
      height: props.height ?? (size ? pxSizeMap[size] : (svgElement.props.height ?? iconPxSize)),
      className: clsx(props.className, flipOnRtl && rtlFlipClassName),
    });
  }

  return createElement(source, {
    ...props,
    width: props.width ?? pxSizeMap[iconSourceSize],
    height: props.height ?? pxSizeMap[iconSourceSize],
    className: clsx(props.className, flipOnRtl && rtlFlipClassName),
  });
});
